import { useLayoutEffect } from 'react';
import gsap from 'gsap';

/**
 * Polished hero entrance:
 *   - Heading: split into chunks (text-words + element spans kept whole, so
 *     gradient-text spans stay intact). Each chunk reveals via clip-mask +
 *     y-translate + blur with a long power4.out ease.
 *   - Other elements: smooth fade + y + blur stagger.
 *
 * Pass refs in visual order. Mark heading as { ref, heading: true }.
 */
export default function useHeroEntrance(items = []) {
  useLayoutEffect(() => {
    const id = setTimeout(() => {
      const ctx = gsap.context(() => {
        const tl = gsap.timeline({ delay: 0.05 });

        items.forEach((item) => {
          const entry = item && item.current !== undefined ? { ref: item } : item;
          const el = entry?.ref?.current;
          if (!el) return;

          if (entry.heading && !el.dataset.split) {
            // Walk *direct* children. Text nodes -> split into word spans.
            // Element nodes (e.g. <span class="gradient-text">) -> kept whole
            // so background-clip:text gradients render across the full word.
            const chunks = [];
            const newChildren = [];
            Array.from(el.childNodes).forEach((node) => {
              if (node.nodeType === Node.TEXT_NODE) {
                const parts = node.textContent.split(/(\s+)/);
                parts.forEach((w) => {
                  if (!w) return;
                  if (/^\s+$/.test(w)) {
                    newChildren.push(document.createTextNode(w));
                  } else {
                    const outer = document.createElement('span');
                    outer.style.display = 'inline-block';
                    outer.style.overflow = 'hidden';
                    outer.style.verticalAlign = 'baseline';
                    outer.style.lineHeight = 'inherit';
                    outer.style.paddingBottom = '0.15em';
                    outer.style.marginBottom = '-0.15em';
                    const inner = document.createElement('span');
                    inner.className = 'hero-chunk';
                    inner.style.display = 'inline-block';
                    inner.style.willChange = 'transform, opacity, filter';
                    inner.textContent = w;
                    outer.appendChild(inner);
                    newChildren.push(outer);
                    chunks.push(inner);
                  }
                });
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                // Wrap element child in a clip-mask outer; element keeps its
                // own classes (gradient-text etc.) intact.
                const outer = document.createElement('span');
                outer.style.display = 'inline-block';
                outer.style.overflow = 'hidden';
                outer.style.verticalAlign = 'baseline';
                outer.style.lineHeight = 'inherit';
                outer.style.paddingBottom = '0.15em';
                outer.style.marginBottom = '-0.15em';
                const wrap = document.createElement('span');
                wrap.className = 'hero-chunk';
                wrap.style.display = 'inline-block';
                wrap.style.willChange = 'transform, opacity, filter';
                wrap.appendChild(node);
                outer.appendChild(wrap);
                newChildren.push(outer);
                chunks.push(wrap);
              } else {
                newChildren.push(node);
              }
            });

            el.innerHTML = '';
            newChildren.forEach((n) => el.appendChild(n));
            el.dataset.split = '1';

            gsap.set(chunks, {
              yPercent: 115,
              opacity: 0,
              rotate: 3,
              filter: 'blur(8px)',
            });
            tl.to(
              chunks,
              {
                yPercent: 0,
                opacity: 1,
                rotate: 0,
                filter: 'blur(0px)',
                duration: 1.1,
                stagger: 0.08,
                ease: 'power4.out',
              },
              0
            );
          } else {
            gsap.set(el, { opacity: 0, y: 28, filter: 'blur(6px)' });
            tl.to(
              el,
              {
                opacity: 1,
                y: 0,
                filter: 'blur(0px)',
                duration: 1,
                ease: 'power3.out',
              },
              '-=0.75'
            );
          }
        });
      });

      return () => ctx.revert();
    }, 80);

    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
